import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    Button,
    Checkbox,
    Input,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider,
} from '@pagespeedify/ui';
import {
    Search,
    Monitor,
    Smartphone,
    PauseCircle,
    PlayCircle,
    Loader2,
    Navigation,
    Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../lib/apiClient';
import { MonitoredUrl } from '../../types';

interface GroupedUrl {
    websiteUrl: string;
    mobile: { id: string; is_active: boolean } | null;
    desktop: { id: string; is_active: boolean } | null;
}

interface QuickSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    groupedUrls: Map<string, { mobile?: { id: string; is_active: boolean }; desktop?: { id: string; is_active: boolean } }>;
    monitoredUrls: MonitoredUrl[];
    selectedWebsiteUrl: string | null;
    onSelectUrl: (url: string) => void;
    onSuccess: () => void;
}

export const QuickSettingsModal: React.FC<QuickSettingsModalProps> = ({
    isOpen,
    onClose,
    groupedUrls,
    monitoredUrls,
    selectedWebsiteUrl,
    onSelectUrl,
    onSuccess,
}) => {
    const [activeTab, setActiveTab] = useState<'navigate' | 'manage'>('navigate');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const apiClient = useApi();
    const queryClient = useQueryClient();

    // Batch mutation
    const batchMutation = useMutation({
        mutationFn: ({ action, ids }: { action: 'pause' | 'resume'; ids: string[] }) =>
            apiClient.post<{ success: boolean; data?: { processed: number }; message?: string }>('/monitoring/batch', {
                action,
                ids,
            }),
        onSuccess: (response, variables) => {
            const count = response.data?.processed || variables.ids.length;
            toast.success(`Successfully ${variables.action === 'pause' ? 'paused' : 'resumed'} ${count} URL(s)`);
            queryClient.invalidateQueries({ queryKey: ['monitoredUrls'] });
            onSuccess();
            setSelectedIds(new Set());
        },
        onError: (error: any, variables) => {
            console.error(`Batch ${variables.action} error:`, error);
            toast.error(error.message || `Failed to ${variables.action} URLs`);
        },
    });

    // Convert Map to array for navigation tab
    const urlEntries = useMemo(() => {
        const entries: GroupedUrl[] = [];
        groupedUrls.forEach((value, key) => {
            entries.push({
                websiteUrl: key,
                mobile: value.mobile || null,
                desktop: value.desktop || null,
            });
        });
        return entries;
    }, [groupedUrls]);

    // Filter URLs for navigation tab
    const filteredNavUrls = useMemo(() => {
        if (!searchTerm) return urlEntries;
        const term = searchTerm.toLowerCase();
        return urlEntries.filter(entry =>
            entry.websiteUrl.toLowerCase().includes(term)
        );
    }, [urlEntries, searchTerm]);

    // For manage tab, use the same grouped entries
    const filteredManageEntries = useMemo(() => {
        if (!searchTerm) return urlEntries;
        const term = searchTerm.toLowerCase();
        return urlEntries.filter(entry =>
            entry.websiteUrl.toLowerCase().includes(term)
        );
    }, [urlEntries, searchTerm]);

    // Get all IDs for a grouped entry
    const getIdsForEntry = (entry: GroupedUrl): string[] => {
        const ids: string[] = [];
        if (entry.mobile) ids.push(entry.mobile.id);
        if (entry.desktop) ids.push(entry.desktop.id);
        return ids;
    };

    // Check if entry is selected (all its IDs are selected)
    const isEntrySelected = (entry: GroupedUrl): boolean => {
        const ids = getIdsForEntry(entry);
        return ids.length > 0 && ids.every(id => selectedIds.has(id));
    };

    // Check if entry has any ID selected (for showing form factor checkboxes)
    const isEntryPartiallySelected = (entry: GroupedUrl): boolean => {
        const ids = getIdsForEntry(entry);
        return ids.length > 0 && ids.some(id => selectedIds.has(id));
    };

    // Get combined status for a grouped entry
    const getEntryStatus = (entry: GroupedUrl): 'active' | 'paused' | 'mixed' => {
        const mobileActive = entry.mobile?.is_active ?? null;
        const desktopActive = entry.desktop?.is_active ?? null;

        if (mobileActive === null && desktopActive === null) return 'paused';
        if (mobileActive === null) return desktopActive ? 'active' : 'paused';
        if (desktopActive === null) return mobileActive ? 'active' : 'paused';
        if (mobileActive && desktopActive) return 'active';
        if (!mobileActive && !desktopActive) return 'paused';
        return 'mixed';
    };

    // Selection stats for manage tab - count individual selected IDs
    const selectionStats = useMemo(() => {
        let totalSelected = 0;
        let activeCount = 0;
        let pausedCount = 0;

        filteredManageEntries.forEach(entry => {
            // Check mobile
            if (entry.mobile && selectedIds.has(entry.mobile.id)) {
                totalSelected++;
                if (entry.mobile.is_active) {
                    activeCount++;
                } else {
                    pausedCount++;
                }
            }
            // Check desktop
            if (entry.desktop && selectedIds.has(entry.desktop.id)) {
                totalSelected++;
                if (entry.desktop.is_active) {
                    activeCount++;
                } else {
                    pausedCount++;
                }
            }
        });

        return {
            total: totalSelected,
            active: activeCount,
            paused: pausedCount,
        };
    }, [filteredManageEntries, selectedIds]);

    // Reset state when modal opens/closes or tab changes
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setSelectedIds(new Set());
        }
    }, [isOpen]);

    useEffect(() => {
        setSearchTerm('');
        setSelectedIndex(0);
        setTimeout(() => inputRef.current?.focus(), 50);
    }, [activeTab]);

    // Reset selection when filter changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [filteredNavUrls]);

    // Keyboard navigation (only for navigate tab)
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (activeTab !== 'navigate') return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < filteredNavUrls.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredNavUrls[selectedIndex]) {
                    handleSelectUrl(filteredNavUrls[selectedIndex].websiteUrl);
                }
                break;
            case 'Escape':
                e.preventDefault();
                onClose();
                break;
        }
    };

    // Scroll selected item into view
    useEffect(() => {
        if (listRef.current && activeTab === 'navigate') {
            const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex, activeTab]);

    const handleSelectUrl = (url: string) => {
        onSelectUrl(url);
        onClose();
    };

    const formatUrl = (url: string, maxLength: number = 60) => {
        const cleaned = url.replace(/^https?:\/\/(www\.)?/, '');
        if (cleaned.length <= maxLength) return cleaned;
        return cleaned.substring(0, maxLength - 3) + '...';
    };

    // Manage tab handlers
    const handleToggleAll = (checked: boolean) => {
        if (checked) {
            const allIds = new Set<string>();
            filteredManageEntries.forEach(entry => {
                getIdsForEntry(entry).forEach(id => allIds.add(id));
            });
            setSelectedIds(allIds);
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleToggleEntry = (entry: GroupedUrl) => {
        const entryIds = getIdsForEntry(entry);
        setSelectedIds(prev => {
            const next = new Set(prev);
            const allSelected = entryIds.every(id => next.has(id));
            if (allSelected) {
                // Deselect all
                entryIds.forEach(id => next.delete(id));
            } else {
                // Select all
                entryIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    // Toggle a single form factor ID
    const toggleSingleId = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleBatchAction = (action: 'pause' | 'resume') => {
        if (selectedIds.size === 0) {
            toast.error('Please select at least one URL');
            return;
        }
        batchMutation.mutate({ action, ids: Array.from(selectedIds) });
    };

    const allFilteredSelected = filteredManageEntries.length > 0 &&
        filteredManageEntries.every(entry => isEntrySelected(entry));

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent hideCloseButton className="w-[95vw] max-w-2xl max-h-[85vh] sm:max-h-[80vh] p-0 gap-0 overflow-hidden flex flex-col">
                {/* Header with Title and Close */}
                <div className="flex items-center justify-between border-b px-4 py-2">
                    <h2 className="text-base font-semibold">Quick Settings</h2>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={onClose}
                                    className="p-1.5 rounded-md hover:bg-muted transition-colors"
                                >
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                                <p>Close (Esc)</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>

                {/* Tab Buttons - like reports page */}
                <div className="flex gap-2 px-4 py-3 border-b">
                    <Button
                        variant={activeTab === 'navigate' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setActiveTab('navigate')}
                        className="gap-1"
                    >
                        <Navigation className="h-3 w-3" />
                        Navigate
                    </Button>
                    <Button
                        variant={activeTab === 'manage' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setActiveTab('manage')}
                        className="gap-1"
                    >
                        <Settings2 className="h-3 w-3" />
                        Manage
                    </Button>
                </div>

                {/* Search Bar */}
                <div className="flex items-center border-b px-3">
                    <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                        ref={inputRef}
                        type="text"
                        placeholder={activeTab === 'navigate' ? 'Search URLs...' : 'Search URLs to manage...'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-9"
                    />
                </div>

                {/* Navigate Tab Content */}
                {activeTab === 'navigate' && (
                    <>
                        <div
                            ref={listRef}
                            className="max-h-[250px] overflow-y-auto overflow-x-hidden"
                        >
                            {filteredNavUrls.length === 0 ? (
                                <div className="px-4 py-8 text-center text-muted-foreground">
                                    No URLs found matching "{searchTerm}"
                                </div>
                            ) : (
                                filteredNavUrls.map((entry, index) => {
                                    const isSelected = index === selectedIndex;
                                    const isCurrent = entry.websiteUrl === selectedWebsiteUrl;
                                    const hasBoth = entry.mobile && entry.desktop;
                                    const anyPaused = (entry.mobile && !entry.mobile.is_active) ||
                                        (entry.desktop && !entry.desktop.is_active);
                                    return (
                                        <button
                                            key={entry.websiteUrl}
                                            onClick={() => handleSelectUrl(entry.websiteUrl)}
                                            className={`
                                                w-full px-3 py-2 flex items-center gap-2 text-left transition-colors text-sm min-w-0
                                                ${isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'}
                                                ${isCurrent ? 'text-primary font-medium' : ''}
                                            `}
                                        >
                                            {/* Device Icons */}
                                            <div className="flex items-center gap-0.5 shrink-0">
                                                {hasBoth ? (
                                                    <>
                                                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                                                        <Monitor className="h-4 w-4 text-muted-foreground" />
                                                    </>
                                                ) : entry.desktop ? (
                                                    <Monitor className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                                                )}
                                                {anyPaused && (
                                                    <PauseCircle className="h-4 w-4 text-amber-500 ml-1" />
                                                )}
                                            </div>

                                            {/* URL */}
                                            <span className="flex-1 min-w-0 truncate" title={entry.websiteUrl}>
                                                {formatUrl(entry.websiteUrl)}
                                            </span>

                                            {/* Current indicator */}
                                            {isCurrent && (
                                                <span className="text-xs text-primary/70 shrink-0">
                                                    Current
                                                </span>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {/* Navigate Footer */}
                        <div className="border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground bg-muted/30">
                            <div className="flex items-center gap-4">
                                <span className="flex items-center gap-1">
                                    <kbd className="inline-flex h-4 select-none items-center rounded border bg-background px-1 font-mono text-[10px]">↑</kbd>
                                    <kbd className="inline-flex h-4 select-none items-center rounded border bg-background px-1 font-mono text-[10px]">↓</kbd>
                                    navigate
                                </span>
                                <span className="flex items-center gap-1">
                                    <kbd className="inline-flex h-4 select-none items-center rounded border bg-background px-1 font-mono text-[10px]">↵</kbd>
                                    select
                                </span>
                            </div>
                            <span>{filteredNavUrls.length} URLs</span>
                        </div>
                    </>
                )}

                {/* Manage Tab Content */}
                {activeTab === 'manage' && (
                    <div className="flex flex-col">
                        {/* Select All */}
                        <div className="flex items-center gap-2 px-4 py-2 border-b">
                            <Checkbox
                                id="select-all"
                                checked={allFilteredSelected}
                                onCheckedChange={handleToggleAll}
                            />
                            <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                                Select all ({filteredManageEntries.length})
                            </label>
                        </div>

                        {/* URL List */}
                        <div className="max-h-[180px] overflow-y-auto overflow-x-hidden p-2">
                            {filteredManageEntries.length === 0 ? (
                                <div className="py-8 text-center text-muted-foreground">
                                    No URLs found
                                </div>
                            ) : (
                                filteredManageEntries.map((entry) => {
                                    const status = getEntryStatus(entry);
                                    const hasBoth = entry.mobile && entry.desktop;

                                    return (
                                        <div key={entry.websiteUrl} className="space-y-1">
                                            <label
                                                className={`
                                                    flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors min-w-0
                                                    hover:bg-muted/50
                                                    ${isEntryPartiallySelected(entry) ? 'bg-primary/10 border border-primary/30' : 'border border-transparent'}
                                                `}
                                            >
                                                <Checkbox
                                                    checked={isEntrySelected(entry)}
                                                    onCheckedChange={() => handleToggleEntry(entry)}
                                                />

                                                {/* Device Icons */}
                                                <div className="flex items-center gap-0.5 shrink-0">
                                                    {hasBoth ? (
                                                        <>
                                                            <Smartphone className="h-4 w-4 text-muted-foreground" />
                                                            <Monitor className="h-4 w-4 text-muted-foreground" />
                                                        </>
                                                    ) : entry.desktop ? (
                                                        <Monitor className="h-4 w-4 text-muted-foreground" />
                                                    ) : (
                                                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                </div>

                                                {/* URL */}
                                                <span className="flex-1 min-w-0 truncate text-sm" title={entry.websiteUrl}>
                                                    {formatUrl(entry.websiteUrl)}
                                                </span>

                                                {/* Status */}
                                                {status === 'active' ? (
                                                    <span className="flex items-center gap-1 text-xs text-green-500 shrink-0 whitespace-nowrap">
                                                        <PlayCircle className="h-3 w-3" /> Active
                                                    </span>
                                                ) : status === 'paused' ? (
                                                    <span className="flex items-center gap-1 text-xs text-amber-500 shrink-0 whitespace-nowrap">
                                                        <PauseCircle className="h-3 w-3" /> Paused
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-xs text-blue-500 shrink-0 whitespace-nowrap">
                                                        <PauseCircle className="h-3 w-3" /> Mixed
                                                    </span>
                                                )}
                                            </label>

                                            {/* Form Factor Selection (shown when URL has both and any is selected) */}
                                            {hasBoth && isEntryPartiallySelected(entry) && (
                                                <div className="ml-8 mb-2 flex gap-4 text-xs">
                                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                                        <Checkbox
                                                            checked={selectedIds.has(entry.mobile!.id)}
                                                            onCheckedChange={() => toggleSingleId(entry.mobile!.id)}
                                                        />
                                                        <Smartphone className="h-3 w-3" />
                                                        <span>Mobile</span>
                                                        <span className={entry.mobile!.is_active ? 'text-green-500' : 'text-amber-500'}>
                                                            ({entry.mobile!.is_active ? 'active' : 'paused'})
                                                        </span>
                                                    </label>
                                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                                        <Checkbox
                                                            checked={selectedIds.has(entry.desktop!.id)}
                                                            onCheckedChange={() => toggleSingleId(entry.desktop!.id)}
                                                        />
                                                        <Monitor className="h-3 w-3" />
                                                        <span>Desktop</span>
                                                        <span className={entry.desktop!.is_active ? 'text-green-500' : 'text-amber-500'}>
                                                            ({entry.desktop!.is_active ? 'active' : 'paused'})
                                                        </span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Manage Footer */}
                        <div className="border-t px-4 py-3 flex items-center justify-between bg-muted/30">
                            {selectedIds.size > 0 ? (
                                <span className="text-xs text-muted-foreground">
                                    {selectionStats.total} selected ({selectionStats.active} active, {selectionStats.paused} paused)
                                </span>
                            ) : (
                                <span className="text-xs text-muted-foreground">
                                    Select URLs to pause or resume
                                </span>
                            )}
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleBatchAction('resume')}
                                    disabled={batchMutation.status === 'pending' || selectionStats.paused === 0}
                                    className="text-green-500 border-green-500/50 hover:bg-green-500/10 hover:border-green-500"
                                >
                                    {batchMutation.status === 'pending' ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <PlayCircle className="mr-1.5 h-3 w-3" />}
                                    Resume
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleBatchAction('pause')}
                                    disabled={batchMutation.status === 'pending' || selectionStats.active === 0}
                                    className="text-amber-500 border-amber-500/50 hover:bg-amber-500/10 hover:border-amber-500"
                                >
                                    {batchMutation.status === 'pending' ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <PauseCircle className="mr-1.5 h-3 w-3" />}
                                    Pause
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog >
    );
};

export default QuickSettingsModal;
